import React, { useState, useEffect } from 'react';
import { X, Save, ClipboardList, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth } from '../utils/api';
import PrescriptionEditor from './PrescriptionEditor';
import ProductSaleEditor from './ProductSaleEditor';

const BLANK_NOTE = {
  visit_date: new Date().toISOString().split('T')[0],
  dentist_id: '',
  chief_complaint: '',
  clinical_findings: '',
  diagnosis: '',
  treatment_performed: '',
  treatment_id: '',
  tooth_number: '',
  medications: '',
  next_appointment: '',
  notes: '',
};

// A saved prescription/sale line comes back from the API with extra columns
// (item_id, net_amount, vat_amount, total_price, status, ...) the editors
// don't know about. Keep only the fields the editors read/write so a resaved
// line looks exactly like a freshly-added one.
const toRxLine = (it) => ({
  medication_id: it.medication_id,
  quantity_prescribed: it.quantity_prescribed,
  dose_per_time: it.dose_per_time,
  frequency: it.frequency,
  duration_days: it.duration_days,
  administration_route: it.administration_route,
  before_food: it.before_food,
  after_food: it.after_food,
  meal_times: it.meal_times || '',
  special_instructions: it.special_instructions || '',
  unit_price: it.unit_price,
  vat_mode: it.vat_mode,
  vat_rate: it.vat_rate,
  // Kept for the same reason toSaleLine keeps it: without it, re-saving a note
  // silently zeroes any per-line prescription discount.
  discount_amount: it.discount_amount,
});

const toSaleLine = (it) => ({
  product_id: it.product_id,
  quantity: it.quantity,
  unit_price: it.unit_price,
  vat_mode: it.vat_mode,
  vat_rate: it.vat_rate,
  discount_amount: it.discount_amount,
});

// Everything the dentist has typed, in one comparable string. Used only to
// answer "is there anything here to lose?" before the modal closes.
const noteFingerprint = (form, rx, sale) => JSON.stringify({ form, rx, sale });

const ProgressNoteModal = ({
  isOpen, onClose, onSaved,
  patient, dentists, branchId, branchVatRate,
  note,                  // null for a new note; the note object when editing
  defaultDentistId,      // used for a NEW note only: the dentist writing it
}) => {
  const { print_lang } = useLanguage();
  const isEdit = !!note;

  const [noteFormData, setNoteFormData] = useState(BLANK_NOTE);
  const [rxLines, setRxLines] = useState([]);
  const [saleLines, setSaleLines] = useState([]);
  const [rxId, setRxId] = useState(null);
  const [saleId, setSaleId] = useState(null);
  const [showRx, setShowRx] = useState(false);
  const [showSale, setShowSale] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  // Fingerprint of the note exactly as it was loaded, and the confirmation
  // that stands between an accidental dismissal and losing the note. On a
  // tablet the overlay is a large target a resting hand finds easily, and it
  // discarded the note AND its prescription and product lines with no warning.
  const [pristine, setPristine] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  // A note's own free-text fields can still be saved after its prescription or
  // sale has been dispensed/confirmed — only the medication/product lines are
  // locked. That status lives on the note itself (from the patient's notes
  // list), not on the line-item detail fetched below.
  //
  // The two are tracked SEPARATELY. One shared flag meant filling a
  // prescription also froze a still-pending sale on the same note, so a
  // product the patient had not yet been handed could never be corrected.
  const rxDispensed = !!(
    note?.prescription?.status && note.prescription.status !== 'pending'
  );
  const saleDispensed = !!(
    note?.product_sale?.status && note.product_sale.status !== 'pending'
  );

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setConfirmDiscard(false);

    if (!note) {
      // `??` (not `||`) so a legitimate dentist id of 0 would survive.
      const blank = { ...BLANK_NOTE, dentist_id: defaultDentistId ?? '' };
      setNoteFormData(blank);
      setRxLines([]);
      setSaleLines([]);
      setRxId(null);
      setSaleId(null);
      setShowRx(false);
      setShowSale(false);
      setPristine(noteFingerprint(blank, [], []));
      return;
    }

    const loaded = {
      visit_date: note.visit_date ? note.visit_date.split('T')[0] : '',
      dentist_id: note.dentist_id ?? defaultDentistId ?? '',
      chief_complaint: note.chief_complaint || '',
      clinical_findings: note.clinical_findings || '',
      diagnosis: note.diagnosis || '',
      treatment_performed: note.treatment_performed || '',
      treatment_id: note.treatment_id || '',
      tooth_number: note.tooth_number || '',
      medications: note.medications || '',
      next_appointment: note.next_appointment ? note.next_appointment.split('T')[0] : '',
      notes: note.notes || '',
    };
    setNoteFormData(loaded);
    // Provisional: the lines are still in flight. Re-taken below once they
    // land, so a note that merely finished loading never reads as edited.
    setPristine(noteFingerprint(loaded, [], []));

    // The notes list only carries prescription/sale summaries (status,
    // number, total). The line items needed to edit them come from the
    // single-note GET.
    fetchWithAuth(`/api/progress-notes/${note.note_id}`)
      .then((r) => {
        const rx = r?.prescription;
        const sale = r?.product_sale;
        const rxItems = rx ? rx.items.map(toRxLine) : [];
        const saleItems = sale ? sale.items.map(toSaleLine) : [];
        setRxLines(rxItems);
        setSaleLines(saleItems);
        setRxId(rx ? rx.prescription_id : null);
        setSaleId(sale ? sale.sale_id : null);
        setShowRx(!!rx && rx.items.length > 0);
        setShowSale(!!sale && sale.items.length > 0);
        setPristine(noteFingerprint(loaded, rxItems, saleItems));
      })
      .catch(() => {
        setRxLines([]);
        setSaleLines([]);
        setRxId(null);
        setSaleId(null);
        setPristine(noteFingerprint(loaded, [], []));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, note]);

  const isDirty = noteFingerprint(noteFormData, rxLines, saleLines) !== pristine;

  // Every way out of this modal that is not Save goes through here, so an
  // accidental overlay tap and a mis-aimed X behave the same way. With nothing
  // typed it closes immediately -- the confirmation only appears when there is
  // actually something to lose.
  const requestClose = () => {
    if (isDirty) {
      setConfirmDiscard(true);
      return;
    }
    onClose();
  };

  const handleSaveNote = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError('');
    try {
      if (isEdit) {
        const payload = {
          ...noteFormData,
          patient_id: patient.patient_id,
          branch_id: branchId,
        };
        const result = await fetchWithAuth(`/api/progress-notes/${note.note_id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });

        // The API already owns the replace-while-pending rule for prescriptions
        // and sales (Task 4 Step 1, Task 5 Step 3) — only call it for the
        // record the API did not report as locked. The prescription and the
        // sale are gated independently: a dispensed prescription must not
        // block an edit to a sale that is still pending.
        //
        // Four states, not two: create, replace, CLEAR, or do nothing.
        // Clearing matters — a dentist who deletes every line and saves must
        // not leave a prescription standing that the note no longer shows.
        //
        // `?? result?.locked` keeps this working against an API that predates
        // the split and still answers with the single flag.
        const rxLocked = result?.locked_prescription ?? result?.locked;
        const saleLocked = result?.locked_sale ?? result?.locked;

        if (!rxLocked) {
          if (rxId) {
            await fetchWithAuth(`/api/prescriptions/${rxId}`, {
              method: 'PUT',
              body: JSON.stringify({ items: rxLines }),   // [] legitimately clears it
            });
          } else if (rxLines.length > 0) {
            await fetchWithAuth('/api/prescriptions', {
              method: 'POST',
              body: JSON.stringify({
                branch_id: branchId,
                patient_id: patient.patient_id,
                note_id: note.note_id,
                dentist_id: noteFormData.dentist_id ?? null,
                prescription_date: noteFormData.visit_date,
                items: rxLines,
              }),
            });
          }
        }

        if (!saleLocked) {
          if (saleId) {
            await fetchWithAuth(`/api/shop/sales/${saleId}`, {
              method: 'PUT',
              body: JSON.stringify({ items: saleLines }),   // [] legitimately clears it
            });
          } else if (saleLines.length > 0) {
            await fetchWithAuth('/api/shop/sales', {
              method: 'POST',
              body: JSON.stringify({
                branch_id: branchId,
                patient_id: patient.patient_id,
                note_id: note.note_id,
                dentist_id: noteFormData.dentist_id ?? null,
                sale_date: noteFormData.visit_date,
                items: saleLines,
              }),
            });
          }
        }
      } else {
        const body = {
          ...noteFormData,
          patient_id: patient.patient_id,
          branch_id: branchId,
          ...(rxLines.length > 0 && { prescription: { items: rxLines } }),
          ...(saleLines.length > 0 && { product_sale: { items: saleLines } }),
        };
        await fetchWithAuth('/api/progress-notes', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      }

      onSaved();
    } catch (err) {
      console.error('Failed to save note:', err);
      // Prefer the translated string; the server's raw English message is only
      // the fallback, so a Thai operator does not see it as the primary text.
      setError(print_lang('save_failed', err.message || 'Save failed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={requestClose}>
      <div className="modal-content w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <ClipboardList size={24} />
            {isEdit
              ? print_lang('edit_progress_note', 'Edit Progress Note')
              : print_lang('add_progress_note', 'Add Progress Note')
            }
          </h2>
          <button onClick={requestClose}>
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 flex items-center gap-2">
            <AlertTriangle size={16} /> {error}
          </div>
        )}

        <form onSubmit={handleSaveNote} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {print_lang('visit_date', 'Visit Date')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={noteFormData.visit_date}
                onChange={(e) => setNoteFormData({ ...noteFormData, visit_date: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {print_lang('dentist', 'Dentist')}
              </label>
              <select
                value={noteFormData.dentist_id}
                onChange={(e) => setNoteFormData({ ...noteFormData, dentist_id: e.target.value })}
              >
                <option value="">{print_lang('select_dentist', 'Select Dentist')}</option>
                {dentists.map((dentist) => (
                  <option key={dentist.dentist_id} value={dentist.dentist_id}>
                    {dentist.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {print_lang('chief_complaint', 'Chief Complaint')}
            </label>
            <textarea
              value={noteFormData.chief_complaint}
              onChange={(e) => setNoteFormData({ ...noteFormData, chief_complaint: e.target.value })}
              rows={2}
              placeholder={print_lang('chief_complaint_placeholder', 'Patient\'s main concern or reason for visit...')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {print_lang('clinical_findings', 'Clinical Findings')}
            </label>
            <textarea
              value={noteFormData.clinical_findings}
              onChange={(e) => setNoteFormData({ ...noteFormData, clinical_findings: e.target.value })}
              rows={2}
              placeholder={print_lang('clinical_findings_placeholder', 'Examination findings...')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {print_lang('diagnosis', 'Diagnosis')}
              </label>
              <textarea
                value={noteFormData.diagnosis}
                onChange={(e) => setNoteFormData({ ...noteFormData, diagnosis: e.target.value })}
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {print_lang('treatment_performed', 'Treatment Performed')}
              </label>
              <textarea
                value={noteFormData.treatment_performed}
                onChange={(e) => setNoteFormData({ ...noteFormData, treatment_performed: e.target.value })}
                rows={2}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {print_lang('tooth_number', 'Tooth Number')}
              </label>
              <input
                type="text"
                value={noteFormData.tooth_number}
                onChange={(e) => setNoteFormData({ ...noteFormData, tooth_number: e.target.value })}
                placeholder="e.g., 11, 21, 36"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                {print_lang('medications_prescribed', 'Medications Prescribed')}
              </label>
              <input
                type="text"
                value={noteFormData.medications}
                onChange={(e) => setNoteFormData({ ...noteFormData, medications: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {print_lang('next_appointment', 'Next Appointment')}
            </label>
            <input
              type="date"
              value={noteFormData.next_appointment}
              onChange={(e) => setNoteFormData({ ...noteFormData, next_appointment: e.target.value })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              {print_lang('additional_notes', 'Additional Notes')}
            </label>
            <textarea
              value={noteFormData.notes}
              onChange={(e) => setNoteFormData({ ...noteFormData, notes: e.target.value })}
              rows={3}
            />
          </div>

          {(rxDispensed || saleDispensed) && (
            <div className="bg-amber-50 border border-amber-300 text-amber-800 px-3 py-2 rounded mb-3 text-sm">
              {rxDispensed && saleDispensed
                ? print_lang('locked_dispensed', 'Already dispensed — edit is locked')
                : rxDispensed
                  ? print_lang('locked_dispensed_rx', 'Medications already dispensed — that section is locked')
                  : print_lang('locked_dispensed_sale', 'Products already sold — that section is locked')}
            </div>
          )}

          <div className="border-t pt-4 mt-4">
            <button type="button" className="flex items-center gap-2 font-semibold mb-3"
                    onClick={() => setShowRx((v) => !v)}>
              {showRx ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              {print_lang('medications_prescribed', 'Medications Prescribed')}
              {rxLines.length > 0 && <span className="badge">{rxLines.length}</span>}
            </button>
            {showRx && (
              <PrescriptionEditor
                value={rxLines} onChange={setRxLines}
                branchId={branchId} branchVatRate={branchVatRate}
                disabled={rxDispensed}
              />
            )}
          </div>

          <div className="border-t pt-4 mt-4">
            <button type="button" className="flex items-center gap-2 font-semibold mb-3"
                    onClick={() => setShowSale((v) => !v)}>
              {showSale ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              {print_lang('products_sold', 'Products Sold')}
              {saleLines.length > 0 && <span className="badge">{saleLines.length}</span>}
            </button>
            {showSale && (
              <ProductSaleEditor
                value={saleLines} onChange={setSaleLines}
                branchId={branchId} branchVatRate={branchVatRate}
                disabled={saleDispensed}
              />
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={requestClose}
              className="btn btn-secondary"
            >
              {print_lang('cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="btn btn-primary flex items-center gap-2"
            >
              <Save size={18} />
              {isSaving ? print_lang('saving', 'Saving...') : print_lang('save', 'Save')}
            </button>
          </div>
        </form>

        {/* Nested inside .modal-content so it inherits that element's stacking
            context and paints over the note behind it. In-page, not
            window.confirm: this modal runs on a tablet. */}
        {confirmDiscard && (
          <div className="modal-overlay" onClick={(e) => {
            if (e.target === e.currentTarget) setConfirmDiscard(false);
          }}>
            <div className="modal-content w-full max-w-sm p-6">
              <h3 className="text-lg font-bold flex items-center gap-2 mb-3">
                <AlertTriangle size={22} className="text-amber-600" />
                {print_lang('discard_note_title', 'Discard this note?')}
              </h3>
              <p className="text-sm text-gray-700 mb-6">
                {print_lang('discard_note_warning',
                  'This note has unsaved changes, including any prescription or product lines. Closing now will discard them.')}
              </p>
              <div className="flex justify-end gap-3">
                <button type="button" className="btn btn-secondary"
                        onClick={() => setConfirmDiscard(false)}>
                  {print_lang('keep_editing', 'Keep editing')}
                </button>
                <button type="button" className="btn btn-danger"
                        onClick={() => { setConfirmDiscard(false); onClose(); }}>
                  {print_lang('discard', 'Discard')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProgressNoteModal;
